import React, { useCallback, useEffect, useState } from 'react';
import { apiGet, apiPut } from '../../lib/api';
import { formatINR } from '../../lib/format';
import { toast } from 'sonner';
import { Percent, Settings2, X, Info } from 'lucide-react';

const inp = 'w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]';
const lbl = 'block text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500 mb-1';
const TH = 'px-3 py-2 text-left text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';
const THR = 'px-3 py-2 text-right text-[10px] font-display font-bold uppercase tracking-wider text-slate-500';

function currentFY() {
  const t = new Date();
  const sy = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1;
  return `${sy}-${String((sy + 1) % 100).padStart(2, '0')}`;
}

const Tile = ({ label, value, tone = 'default' }) => {
  const tones = { default: 'border-slate-200', amber: 'border-amber-300 bg-amber-50', rose: 'border-rose-300 bg-rose-50' };
  return (
    <div className={`border rounded-sm px-4 py-2.5 ${tones[tone]}`}>
      <div className="text-[10px] font-display font-bold uppercase tracking-[0.14em] text-slate-500">{label}</div>
      <div className="font-display text-lg font-extrabold text-[#1F7A6E]">{value}</div>
    </div>
  );
};

function ConfigDrawer({ cfg, onClose, onSaved }) {
  const [f, setF] = useState({ ...cfg });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await apiPut('/finance/tcs/config', f);
      toast.success('TCS config updated');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/30" data-testid="tcs-config-drawer">
      <div className="bg-white h-full w-[420px] max-w-full p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg font-extrabold">TCS Config</h2>
          <button onClick={onClose} className="text-slate-400"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className={lbl}>Threshold per buyer / FY (₹)</label>
            <input type="number" className={inp} value={f.threshold_per_buyer_annual}
              onChange={(e) => setF({ ...f, threshold_per_buyer_annual: e.target.value })} data-testid="tcs-cfg-threshold" />
          </div>
          <div>
            <label className={lbl}>Rate — buyer PAN on file (%)</label>
            <input type="number" step="0.01" className={inp} value={f.rate_pct}
              onChange={(e) => setF({ ...f, rate_pct: e.target.value })} data-testid="tcs-cfg-rate" />
          </div>
          <div>
            <label className={lbl}>Rate — buyer PAN missing (%)</label>
            <input type="number" step="0.01" className={inp} value={f.rate_no_pan_pct}
              onChange={(e) => setF({ ...f, rate_no_pan_pct: e.target.value })} data-testid="tcs-cfg-rate-no-pan" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button className="px-4 py-2 text-xs font-bold uppercase border border-slate-300 rounded-sm" onClick={onClose}>Cancel</button>
          <button className="px-4 py-2 text-xs font-bold uppercase text-white bg-[#1F7A6E] rounded-sm" onClick={save} disabled={saving} data-testid="tcs-cfg-save">Save</button>
        </div>
      </div>
    </div>
  );
}

export default function TCSCenter() {
  const [fy, setFy] = useState(currentFY());
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);

  const load = useCallback(() => {
    setData(null);
    apiGet('/finance/tcs/register', { fy }).then(setData).catch(() => toast.error('Failed to load TCS register'));
  }, [fy]);
  const loadCfg = useCallback(() => { apiGet('/finance/tcs/config').then(setCfg).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCfg(); }, [loadCfg]);

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="tcs-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Finance & Accounts</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2"><Percent size={26} className="text-[#1F7A6E]" /> TCS Register</h1>
          <p className="text-sm text-slate-600 mt-1">Section 206C(1H) — TCS on sale consideration received, buyer-wise, once cumulative receipts in a FY cross the threshold.</p>
        </div>
        <div className="flex items-center gap-2">
          <input className={inp} style={{ width: 110 }} value={fy} onChange={(e) => setFy(e.target.value)} placeholder="2026-27" data-testid="tcs-fy-input" />
          <button onClick={() => setShowCfg(true)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase border border-slate-300 rounded-sm hover:bg-slate-50" data-testid="tcs-config-btn">
            <Settings2 size={13} /> Config
          </button>
        </div>
      </div>

      <div className="border border-teal-200 bg-teal-50 rounded-sm px-4 py-2.5 text-xs text-teal-800 flex items-start gap-2" data-testid="tcs-info-banner">
        <Info size={13} className="mt-0.5 shrink-0" /> TCS triggers on receipt of payment, not on billing — this reads approved Receipt Vouchers (Finance → Payments), not Dispatch Challans. Calculation only; your CA files Form 27EQ.
      </div>

      {data && (
        <div className="flex flex-wrap gap-3">
          <Tile label="Buyers Over Threshold" value={data.buyers_over_threshold} tone={data.buyers_over_threshold ? 'amber' : 'default'} />
          <Tile label="Total TCS Liability" value={formatINR(data.total_tcs_liability)} tone={data.total_tcs_liability ? 'rose' : 'default'} />
          <Tile label="Threshold / Buyer / FY" value={formatINR(data.threshold_per_buyer_annual)} />
        </div>
      )}

      <div className="overflow-x-auto border border-slate-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className={TH}>Buyer</th><th className={THR}>Receipts</th><th className={THR}>Receipt Count</th>
              <th className={THR}>Excess Over Threshold</th><th className={TH}>PAN on File?</th>
              <th className={THR}>Rate</th><th className={THR}>TCS Amount</th>
            </tr>
          </thead>
          <tbody>
            {!data && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading…</td></tr>}
            {data && data.items.length === 0 && (
              <tr><td colSpan={7} className="text-center py-8 text-slate-500" data-testid="tcs-empty">
                No buyer has crossed {formatINR(data.threshold_per_buyer_annual)} in receipts this FY — no TCS liability.
              </td></tr>
            )}
            {(data?.items || []).map((r) => (
              <tr key={r.buyer_name} className="border-t border-slate-100" data-testid={`tcs-row-${r.buyer_name}`}>
                <td className="px-3 py-2 font-bold">{r.buyer_name}</td>
                <td className="px-3 py-2 text-right font-display">{formatINR(r.total_receipts)}</td>
                <td className="px-3 py-2 text-right">{r.receipt_count}</td>
                <td className="px-3 py-2 text-right font-display font-bold text-amber-700">{formatINR(r.excess_over_threshold)}</td>
                <td className="px-3 py-2">
                  {r.has_pan
                    ? <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm">yes</span>
                    : <span className="px-2 py-0.5 text-[10px] font-bold uppercase bg-rose-50 text-rose-700 border border-rose-200 rounded-sm">no — higher rate</span>}
                </td>
                <td className="px-3 py-2 text-right">{r.rate_pct}%</td>
                <td className="px-3 py-2 text-right font-display font-bold">{formatINR(r.tcs_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCfg && cfg && (
        <ConfigDrawer cfg={cfg} onClose={() => setShowCfg(false)} onSaved={() => { setShowCfg(false); loadCfg(); load(); }} />
      )}
    </div>
  );
}
